import { useCallback, useEffect, useRef, useState } from "react";
import type { SettingsSaveState } from "@playbookai/ui";
import { useLeaveGuard } from "./leave-guard.js";

/* Draft state for the Settings pages that save through one Save bar
 * (PB-5134, d8/t09). The draft starts from the saved value, `dirty` compares
 * the two, the bar state follows the lifecycle clean → dirty → saving → saved,
 * and the host leave guard is told while the draft is dirty. When the saved
 * value moves (a save landed, a refetch) and the draft is not dirty, the draft
 * follows it. */

export interface SettingsDraft<T> {
  readonly draft: T;
  readonly setDraft: (next: T | ((current: T) => T)) => void;
  readonly dirty: boolean;
  readonly state: SettingsSaveState;
  /** Call around the persistence step; `saved` shows the saved hint until the next edit. */
  readonly beginSave: () => void;
  readonly completeSave: () => void;
  readonly failSave: () => void;
}

export function shallowEqualRecord<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  return keys.every((key) => Object.is(left[key], right[key]));
}

export function useSettingsDraft<T>(
  saved: T,
  isEqual: (a: T, b: T) => boolean = shallowEqualRecord,
): SettingsDraft<T> {
  const [draft, setDraftState] = useState<T>(saved);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const dirty = !isEqual(draft, saved);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const previousSaved = useRef(saved);
  useEffect(() => {
    if (isEqual(previousSaved.current, saved)) return;
    previousSaved.current = saved;
    // A save that landed makes draft and saved equal already; a refetch that
    // moved the saved value while the user has not edited follows it.
    if (!dirtyRef.current) setDraftState(saved);
  }, [saved, isEqual]);

  useLeaveGuard(dirty);

  const setDraft = useCallback((next: T | ((current: T) => T)) => {
    setShowSaved(false);
    setDraftState((current) =>
      typeof next === "function" ? (next as (c: T) => T)(current) : next,
    );
  }, []);
  const beginSave = useCallback(() => {
    setSaving(true);
    setShowSaved(false);
  }, []);
  const completeSave = useCallback(() => {
    setSaving(false);
    setShowSaved(true);
  }, []);
  const failSave = useCallback(() => setSaving(false), []);

  const state: SettingsSaveState = saving
    ? "saving"
    : dirty
      ? "dirty"
      : showSaved
        ? "saved"
        : "clean";

  return { draft, setDraft, dirty, state, beginSave, completeSave, failSave };
}
